import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  children: ReactNode;
};

type ErrorType = 'notification' | 'modal';

type Error = {
  message?: string;
  // modals are flow blocking errors whereas notifications are not
  type?: ErrorType;
};

type ErrorContextType = {
  error: Error | undefined;
  showError: (error?: Error) => void;
  removeError: () => void;
};

const ErrorContext = createContext<ErrorContextType>({} as ErrorContextType);

const useError = (): ErrorContextType => useContext(ErrorContext);

function ErrorProvider({ children }: Props): JSX.Element {
  const { t } = useTranslation();
  const [error, setError] = useState<Error | undefined>();

  const showError = useCallback(
    (errorArg: Error = {}) => {
      const defaultError: Error = {
        message: t('error:generic'),
        type: 'notification',
      };

      setError({
        ...defaultError,
        ...errorArg,
      });
    },
    [t],
  );

  const removeError = useCallback(() => {
    setError(undefined);
  }, []);

  const errorContextValue = useMemo(
    () => ({
      error,
      showError,
      removeError,
    }),
    [error, showError, removeError],
  );
  return (
    <ErrorContext.Provider value={errorContextValue}>
      {children}
    </ErrorContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export default useError;
export { ErrorProvider };
